import { useTranslation } from 'react-i18next';

export default function FinancialSummary() {
  const { t } = useTranslation();

  const handlePrint = () => {
    window.print();
  };

  const handlePDF = async () => {
    const html2pdf = (await import('html2pdf.js')).default;
    const element = document.getElementById('financial-summary-section');
    if (!element) return;
    html2pdf()
      .set({
        margin: 10,
        filename: 'Proposta-Moviis-BRTW260204Lucy.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(element)
      .save();
  };

  const handleWhatsApp = () => {
    const url = window.location.href;
    const msg = encodeURIComponent(
      `Olá! Segue o Catálogo de Equipamentos para Construção Vertical — Moviis:\n${url}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const handleEmail = () => {
    const url = window.location.href;
    const subject = encodeURIComponent('Catálogo de Equipamentos para Construção Vertical — Moviis');
    const body = encodeURIComponent(
      `Olá!\n\nSegue o Catálogo de Equipamentos para Construção Vertical — Moviis:\n${url}\n\nAtenciosamente,\nEquipe Moviis`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <section id="financial-summary-section" className="py-12 sm:py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Título Resumo Financeiro */}
        <div className="text-center mb-3">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {t('financial_title')}
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">{t('financial_subtitle')}</p>
        </div>
        <div className="w-20 h-0.5 bg-orange-500 mx-auto mb-10 sm:mb-14"></div>

        {/* Card Resumo Financeiro */}
        <div className="bg-[#0d1b2a] border border-[#1a2e45] rounded-xl p-6 sm:p-8 mb-8">
          {/* Total do Pedido */}
          <div className="flex justify-between items-center mb-8 pb-6 border-b border-[#1a2e45]">
            <span className="text-blue-200 text-base sm:text-lg font-medium">{t('financial_total_label')}</span>
            <span className="text-3xl sm:text-4xl font-bold text-orange-400">{t('financial_total_value')}</span>
          </div>

          {/* Parcelas */}
          <div className="space-y-4">
            {/* Parcela 1 */}
            <div className="flex items-start gap-4 bg-[#0a1520] rounded-xl p-5">
              <div className="flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-full shrink-0 font-bold text-lg">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                  {t('financial_installment1_label')}
                </h3>
                <p className="text-sm text-blue-200 mb-2">{t('financial_installment1_desc')}</p>
                <p className="text-orange-400 font-bold text-lg">1</p>
              </div>
            </div>

            {/* Parcela 2 */}
            <div className="flex items-start gap-4 bg-[#0a1520] rounded-xl p-5">
              <div className="flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-full shrink-0 font-bold text-lg">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                  {t('financial_installment2_label')}
                </h3>
                <p className="text-sm text-blue-200 mb-2">{t('financial_installment2_desc')}</p>
                <p className="text-orange-400 font-bold text-lg">1</p>
              </div>
            </div>

            {/* Parcela 3 (Saldo) */}
            <div className="flex items-start gap-4 bg-[#0a1520] rounded-xl p-5">
              <div className="flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-full shrink-0 font-bold text-lg">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                  {t('financial_installment3_label')}
                </h3>
                <p className="text-sm text-blue-200 mb-2">{t('financial_installment3_desc')}</p>
                <p className="text-orange-400 font-bold text-lg">1</p>
              </div>
            </div>
          </div>
        </div>

        {/* Considerações da Proposta */}
        <div className="text-center mb-3">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {t('financial_considerations_title')}
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">{t('financial_considerations_subtitle')}</p>
        </div>
        <div className="w-20 h-0.5 bg-orange-500 mx-auto mb-10 sm:mb-14"></div>

        <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 sm:p-8 mb-10">
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-time-line text-orange-500 text-lg"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t('financial_validity_label')}</span>
                <span className="text-gray-600"> {t('financial_validity')}</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-truck-line text-orange-500 text-lg"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t('financial_shipping_label')}</span>
                <span className="text-gray-600"> {t('financial_shipping')}</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-orange-500 text-lg"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t('financial_observation_label')}</span>
                <span className="text-gray-600"> {t('financial_observation')}</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-tools-line text-orange-500 text-lg"></i>
              </div>
              <div>
                <span className="font-semibold text-gray-900">{t('financial_assembly_label')}</span>
                <span className="text-gray-600"> {t('financial_assembly')}</span>
              </div>
            </li>
          </ul>
        </div>

        {/* Botões de Ação */}
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white rounded-lg py-2 px-4 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap text-sm font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-printer-line text-base"></i>
            </div>
            {t('financial_btn_print')}
          </button>

          <button
            onClick={handlePDF}
            className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white rounded-lg py-2 px-4 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap text-sm font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-download-line text-base"></i>
            </div>
            {t('financial_btn_pdf')}
          </button>

          <button
            onClick={handleWhatsApp}
            className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-lg py-2 px-4 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap text-sm font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base"></i>
            </div>
            {t('financial_btn_whatsapp')}
          </button>

          <button
            onClick={handleEmail}
            className="flex items-center gap-2 bg-[#EA4335] hover:bg-[#D33426] text-white rounded-lg py-2 px-4 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md whitespace-nowrap text-sm font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-mail-send-line text-base"></i>
            </div>
            {t('financial_btn_email')}
          </button>
        </div>
      </div>
    </section>
  );
}